import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gift, CheckCircle2 } from "lucide-react";
import { captureAttribution, normalizeNigerianPhone, submitLead, LEAD_DONE_KEY, LEAD_DISMISS_KEY } from "@/lib/leads";
import { trackAdEvent } from "@/lib/adPixels";

const DELAY_MS = 8000;
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // re-show after 3 days if dismissed
const SKIP_PREFIXES = ["/admin", "/seller", "/auth", "/checkout", "/payment", "/order-placed", "/cart"];

export const LeadCapturePopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [interest, setInterest] = useState("buying");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { captureAttribution(); }, []);

  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return;
    if (localStorage.getItem(LEAD_DONE_KEY)) return;
    const dismissed = Number(localStorage.getItem(LEAD_DISMISS_KEY) || 0);
    if (Date.now() - dismissed < SNOOZE_MS) return;
    const delay = new URLSearchParams(window.location.search).has("lead_now") ? 300 : DELAY_MS;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [pathname]);

  const close = (o: boolean) => {
    if (!o && !done) localStorage.setItem(LEAD_DISMISS_KEY, String(Date.now()));
    setOpen(o);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizeNigerianPhone(phone);
    if (!normalized) { setError("Enter a valid Nigerian phone number, e.g. 0803 123 4567"); return; }
    setError(""); setSaving(true);
    try {
      await submitLead(name, normalized, interest);
      trackAdEvent("Lead", {}, normalized);
      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="text-center py-6 space-y-3">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <DialogTitle>You're on the list!</DialogTitle>
            <DialogDescription>We'll send you exclusive deals and new arrivals on WhatsApp/SMS.</DialogDescription>
            <Button onClick={() => setOpen(false)} className="w-full">Continue shopping</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 rounded-full bg-primary/10 p-3"><Gift className="h-6 w-6 text-primary" /></div>
              <DialogTitle className="text-center">Get exclusive deals first</DialogTitle>
              <DialogDescription className="text-center">
                Join our VIP list for early access to discounts and new arrivals from verified Nigerian sellers.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="lead-name">First name (optional)</Label>
                <Input id="lead-name" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} placeholder="Chioma" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lead-phone">WhatsApp / phone number</Label>
                <Input id="lead-phone" type="tel" inputMode="tel" value={phone} maxLength={20} onChange={(e) => setPhone(e.target.value)} placeholder="0803 123 4567" required />
              </div>
              <div className="flex gap-2">
                {[["buying", "I want to buy"], ["selling", "I want to sell"]].map(([v, l]) => (
                  <Button key={v} type="button" variant={interest === v ? "default" : "outline"} className="flex-1" onClick={() => setInterest(v)}>{l}</Button>
                ))}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={saving}>{saving ? "Joining..." : "Join the VIP list"}</Button>
              <p className="text-xs text-muted-foreground text-center">No spam. Unsubscribe anytime.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
